/// <amd-module name="Reporting/Visuals/LayoutComponents/Widgets/ComponentLayoutEngine" />
import * as Resources from "Reporting/Visuals/LayoutComponents/Resources";
import {
    ILayoutComponentFactory,
    MinimalChartLayoutFactory,
    ScalarAndChartLayoutFactory,
    ScalarAndChromelessChartLayoutFactory,
    ScalarAndSubmetricsAndChartLayoutFactory,
    ScalarLayoutFactory
} from "Widgets/LayoutComponents";
import { LayoutState } from "Widgets/LayoutState";

export interface Size {
    /** Width in pixels*/
    width: number;

    /** Height in pixels*/
    height: number;
}

/**
 * Describes a rectangular region in pixels.
 * A size which is bounded within a rect, is considered to be supported by the associated layout.
 */
export interface Rect {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

export class DefaultLayoutRects {
    // Although Widgets are normally restricted to 10 cells in storage,
    // This limit allows for unusually large widget sizes during temporary lightbox scenarios.
    private static readonly maxPixelSize = DefaultLayoutRects.getWidgetDimensionAsPixels(50);

    private static getWidgetDimensionAsPixels(cellCount: number) {
        const cellSize = 160;
        const gutterSize = 10;
        return cellSize + (cellCount - 1) * (cellSize + gutterSize);
    }

    public static oneColumnWide(): Rect {
        return {
            left: 1,
            top: 1,
            right: DefaultLayoutRects.getWidgetDimensionAsPixels(2) - 1,
            bottom: DefaultLayoutRects.maxPixelSize
        };
    }

    // This to be used for header charts scenarios where we want minimal chart rendering
    public static minimal(): Rect {
        return {
            left: 1,
            top: 1,
            right: DefaultLayoutRects.getWidgetDimensionAsPixels(2) - 1,
            bottom: DefaultLayoutRects.maxPixelSize
        };
    }

    public static oneRowTallAtLeastTwoColumnsWide(): Rect {
        return {
            left: DefaultLayoutRects.getWidgetDimensionAsPixels(2),
            top: 1,
            right: DefaultLayoutRects.maxPixelSize,
            bottom: DefaultLayoutRects.getWidgetDimensionAsPixels(2) - 1
        };
    }

    public static atLeastTwoByTwo(): Rect {
        return {
            left: DefaultLayoutRects.getWidgetDimensionAsPixels(2),
            top: DefaultLayoutRects.getWidgetDimensionAsPixels(2),
            right: DefaultLayoutRects.maxPixelSize,
            bottom: DefaultLayoutRects.maxPixelSize
        };
    }
}

/**
 * Provides a check to determine if the layout works for a desired size, and a method to obtain the layout factory
 */
export interface ComponentLayoutMapping {
    /** Describes if the desired size is supported with this layout. */
    isDesiredSizeSupported(size: Size): boolean;

    /** Provides factory for this layout.*/
    getFactory(): ILayoutComponentFactory<LayoutState>;
}

/** Provides a simple implementation for layout mapping, such that instances are just data declarations */
export class SimpleLayoutMapping implements ComponentLayoutMapping {
    private acceptedRect: Rect;
    private factoryMethod: () => ILayoutComponentFactory<LayoutState>;

    constructor(acceptedRect: Rect, factoryMethod: () => ILayoutComponentFactory<LayoutState>) {
        this.acceptedRect = acceptedRect;
        this.factoryMethod = factoryMethod;
    }

    public isDesiredSizeSupported(size: Size): boolean {
        return DefaultComponentLayouts.isSizeInsideRect(size, this.acceptedRect);
    }

    /** Provides factory for this layout.*/
    public getFactory(): ILayoutComponentFactory<LayoutState> {
        return this.factoryMethod();
    }
}

/**
 *  Provides default set of layouts for consumption by ComponentLayoutEngine.
 *  Provides an association between supported size of layout and a factory to construct the layout.
 *  These layouts are not required to use the ComponentLayoutEngine for custom scenarios.
 */
export class DefaultComponentLayouts {
    public static isSizeInsideRect(requestedSize: Size, acceptedArea: Rect) {
        return (
            acceptedArea.left <= requestedSize.width &&
            acceptedArea.right >= requestedSize.width &&
            acceptedArea.top <= requestedSize.height &&
            acceptedArea.bottom >= requestedSize.height
        );
    }

    /** Provides stock set of mappings for scenarios which do not need custom mappings:
     *  narrow -> scalar
     *  Wide & short -> Chromeless chart
     *  Spacious, at least two by two -> Chart
     *  */
    public static getDefaultMappings(): ComponentLayoutMapping[] {
        return [
            new SimpleLayoutMapping(DefaultLayoutRects.oneColumnWide(), () => {
                return new ScalarLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.oneRowTallAtLeastTwoColumnsWide(), () => {
                return new ScalarAndChromelessChartLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.atLeastTwoByTwo(), () => {
                return new ScalarAndChartLayoutFactory();
            })
        ];
    }
}

/**
 *  Provides default set of layouts for consumption by ComponentLayoutEngine.
 *  Provides an association between supported size of layout and a factory to construct the layout.
 *  These layouts are not required to use the ComponentLayoutEngine for custom scenarios.
 */
export class ChartAndSubmetricComponentLayouts extends DefaultComponentLayouts {
    /** Provides stock set of mappings for scenarios which do not need custom mappings:
     *  narrow -> scalar
     *  Wide & short -> Chromeless chart
     *  Spacious, at least two by two -> Chart
     *  */
    public static getDefaultMappings(): ComponentLayoutMapping[] {
        return [
            new SimpleLayoutMapping(DefaultLayoutRects.oneColumnWide(), () => {
                return new ScalarLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.oneRowTallAtLeastTwoColumnsWide(), () => {
                return new ScalarAndChromelessChartLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.atLeastTwoByTwo(), () => {
                return new ScalarAndSubmetricsAndChartLayoutFactory();
            })
        ];
    }
}

/**
 *  Provides default set of layouts for consumption by ComponentLayoutEngine.
 *  Provides an association between supported size of layout and a factory to construct the layout.
 *  These layouts are not required to use the ComponentLayoutEngine for custom scenarios related to header charts and lightbox.
 */
export class MinimalChartAndLightBoxAndReportComponentLayouts extends DefaultComponentLayouts {
    /** Provides stock set of mappings for scenarios which do not need custom mappings:
     *  Spacious, at least two by two -> Chart
     *  */
    public static getDefaultMappings(): ComponentLayoutMapping[] {
        return [
            new SimpleLayoutMapping(DefaultLayoutRects.minimal(), () => {
                return new MinimalChartLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.oneRowTallAtLeastTwoColumnsWide(), () => {
                return new ScalarAndChromelessChartLayoutFactory();
            }),
            new SimpleLayoutMapping(DefaultLayoutRects.atLeastTwoByTwo(), () => {
                return new ScalarAndSubmetricsAndChartLayoutFactory();
            })
        ];
    }
}

/**
 * Responsible for choosing the appropriate factory from multiple alternative LayoutComponents, based on the supplied set of parameters.
 * The first matching occurrence wins.
 */
export class ComponentLayoutEngine {
    public selectLayoutFactory(size: Size, layoutMappings: ComponentLayoutMapping[]): ILayoutComponentFactory<LayoutState> {
        for (let i = 0; i < layoutMappings.length; i++) {
            const currentLayoutMapping = layoutMappings[i];
            if (currentLayoutMapping.isDesiredSizeSupported(size)) {
                return currentLayoutMapping.getFactory();
            }
        }
        throw new Error(Resources.LayoutEngine_NoLayoutError);
    }
}
